
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { DashboardBoardLayout, Task, Rule, Trade } from './types';
import { db, onAuthStateChanged, signInWithGoogle, signOut } from './firebase/firebase';
import Header from './components/Header';
import TaskModal from './components/TaskModal';
import ConfirmationModal from './components/ConfirmationModal';
import { PlusIcon } from './components/icons/PlusIcon';
import useFirestore from './hooks/useFirestore';
import useCollection from './hooks/useCollection';
import useMarketData from './hooks/useMarketData';
import * as Actions from './firebase/actions';
import WatchlistPage from './components/WatchlistPage';
import MarketDashboard from './components/MarketDashboard';
import CoinGeckoPage from './components/CoinGeckoPage';
import RuleSignalPage from './components/RuleSignalPage';
import TradeJournalPage from './components/TradeJournalPage';
import EdgeStatsPage from './components/EdgeStatsPage';
import NewsPage from './components/NewsPage';
import TickerTape from './components/TickerTape';
import DashboardBoard from './components/dashboard/DashboardBoard';
import { DashboardModulesProvider } from './components/dashboard/DashboardModules';

const ADMIN_SESSION_STORAGE_KEY = 'crypto-missions:admin-session';

function hasStoredAdminSession() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(ADMIN_SESSION_STORAGE_KEY) === 'true';
}

function writeAdminSession(enabled: boolean) {
  if (typeof window === 'undefined') {
    return;
  }

  if (enabled) {
    window.localStorage.setItem(ADMIN_SESSION_STORAGE_KEY, 'true');
    return;
  }

  window.localStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
}

function createMockAdminUser(): User {
  return {
    uid: 'admin',
    displayName: 'Admin',
    photoURL: 'https://images.dog.ceo/breeds/hound-afghan/n02088094_1003.jpg',
  } as User;
}

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(() => (
    hasStoredAdminSession() ? createMockAdminUser() : null
  ));
  const [userRole, setUserRole] = useState<'admin' | 'guest' | null>(() => (
    hasStoredAdminSession() ? 'admin' : null
  ));
  const unsubscribeAuth = useRef<() => void | null>(null);

  const handleAuthStateChanged = useCallback(async (currentUser: User | null) => {
    if (currentUser) {
      setUser(currentUser);
      const userDocRef = doc(db, 'users', currentUser.uid);
      const docSnap = await getDoc(userDocRef);
      setUserRole(docSnap.exists() ? docSnap.data().role : 'guest');
    } else {
      setUser(null);
      setUserRole(null);
    }
  }, []);

  const attachAuthListener = useCallback(() => {
    if (unsubscribeAuth.current) {
      unsubscribeAuth.current();
    }

    unsubscribeAuth.current = onAuthStateChanged(handleAuthStateChanged);
  }, [handleAuthStateChanged]);

  useEffect(() => {
    if (hasStoredAdminSession()) {
      setUser(createMockAdminUser());
      setUserRole('admin');
      return () => {
        if (unsubscribeAuth.current) {
          unsubscribeAuth.current();
        }
      };
    }

    attachAuthListener();
    return () => {
      if (unsubscribeAuth.current) {
        unsubscribeAuth.current();
      }
    };
  }, [attachAuthListener]);

  const handleAdminSignIn = () => {
    if (unsubscribeAuth.current) {
      unsubscribeAuth.current();
      unsubscribeAuth.current = null;
    }
    writeAdminSession(true);
    setUser(createMockAdminUser());
    setUserRole('admin');
  };

  const handleSignOut = () => {
    if (user && user.uid === 'admin') {
      writeAdminSession(false);
      setUser(null);
      setUserRole(null);
      attachAuthListener();
    } else {
      signOut();
    }
  };

  const isAdmin = userRole === 'admin' || (user !== null && user.uid === 'admin');

  const tasks = useFirestore<Task[]>('crypto-missions:tasks', []);
  const todosState = useFirestore<unknown>('crypto-missions:todos', null);
  const routinesState = useFirestore<unknown>('crypto-missions:routines', null);
  const notesState = useFirestore<unknown>('crypto-missions:notes', null);
  const notesHeightState = useFirestore<unknown>('crypto-missions:notesHeight', null);
  const quickLinksState = useFirestore<unknown>('crypto-missions:quickLinks', null);
  const leaderboardsState = useFirestore<unknown>('crypto-missions:leaderboards', null);
  const importantDatesState = useFirestore<unknown>('crypto-missions:importantDates', null);
  const dashboardBoardLayout = useFirestore<DashboardBoardLayout | null>('crypto-missions:dashboardBoardLayout', null);

  // 공유 시장 데이터 훅
  const marketData = useMarketData();
  // 룰 목록 (MarketDashboard에서 발동 룰 표시용)
  const rules = useCollection<Rule>('rules', 'name', 'asc');
  // Journal 자동채우기용 prefill 상태
  const [journalPrefill, setJournalPrefill] = useState<Partial<Trade> | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  type PageType = 'dashboard' | 'watchlist' | 'market' | 'coingecko' | 'rules' | 'journal' | 'stats' | 'news';
  const [activePage, setActivePage] = useState<PageType>('dashboard');

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.scrollTo(0, 0);
  }, [activePage]);

  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);

  const handleOpenModal = useCallback((task: Task | null = null) => {
    if (!isAdmin) return;
    setTaskToEdit(task);
    setIsModalOpen(true);
  }, [isAdmin]);

  const handleCloseModal = useCallback(() => {
    setTaskToEdit(null);
    setIsModalOpen(false);
  }, []);

  const createAdminGuardedAction = <T extends any[]>(
    action: (...args: T) => void
  ): ((...args: T) => void) => {
    return (...args: T) => {
      if (!isAdmin) {
        console.warn("Action blocked: User is not an admin.");
        return;
      }
      action(...args);
    };
  };

  const handleSaveTaskCallback = createAdminGuardedAction(async (task: Task) => {
    await Actions.handleSaveTask(task, tasks);
    handleCloseModal();
  });

  const handleConfirmDeleteCallback = createAdminGuardedAction(async () => {
    if (!taskToDelete) return;
    await Actions.handleDeleteTask(taskToDelete, tasks);
    setTaskToDelete(null);
  });

  const handleExportData = useCallback(() => {
    const dataToExport = {
      tasks,
      todos: todosState,
      notes: notesState,
      notesHeight: notesHeightState,
      quickLinks: quickLinksState,
      leaderboards: leaderboardsState,
      importantDates: importantDatesState,
      routines: routinesState,
      dashboardBoardLayout,
    };
    const jsonString = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mission-control-backup.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [tasks, todosState, notesState, notesHeightState, quickLinksState, leaderboardsState, importantDatesState, routinesState, dashboardBoardLayout]);

  const handleImportData = createAdminGuardedAction((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const importedData = JSON.parse(text);
        if (window.confirm("Are you sure you want to overwrite ALL current data with the backup file?")) {
          await Actions.importData(importedData);
        }
      } catch (error) {
        console.error("Import error:", error);
        alert("Error importing data. Please check the file format.");
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  });

  const handleToggleCompleteCallback = createAdminGuardedAction((taskId: string) => Actions.handleToggleComplete(taskId, tasks));

  return (
    <div className="app-shell-zoom-80 page-density-90 min-h-screen">
      <Header
        user={user}
        onSignIn={signInWithGoogle}
        onSignOut={handleSignOut}
        onExport={handleExportData}
        onImport={handleImportData}
        isAdmin={isAdmin}
        onAdminSignIn={handleAdminSignIn}
      />
      {/* 티커 테이프 */}
      <div className="dashboard-topbar-surface sticky z-20" style={{ top: 'var(--app-topbar-offset, 60px)' }}>
        <TickerTape />
      </div>
      {/* 최상위 탭 네비게이션 */}
      <div className="dashboard-nav-surface sticky z-10" style={{ top: 'var(--app-nav-offset, 106px)' }}>
        <div className="w-full px-4 md:px-8">
          <div className="flex gap-1 py-1 overflow-x-auto">
            {([
              { id: 'dashboard', label: '🏠 대시보드' },
              { id: 'watchlist', label: '⭐ 내 추적' },
              { id: 'market', label: '📊 마켓' },
              { id: 'coingecko', label: 'CoinGecko' },
              { id: 'rules', label: '🎯 룰 시그널' },
              { id: 'journal', label: '📝 트레이드' },
              { id: 'stats', label: '📈 통계' },
              { id: 'news', label: '📰 뉴스' },
            ] as { id: PageType; label: string }[]).map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setActivePage(id)}
                className={`flex h-9 items-center gap-1.5 rounded-lg px-4 text-sm font-bold transition-all whitespace-nowrap ${activePage === id
                  ? 'dashboard-nav-tab-active'
                  : 'dashboard-nav-tab-idle'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <main className="w-full px-2 pb-4 pt-3 md:px-4 md:pb-8 md:pt-3">
        {activePage === 'watchlist' ? (
          <WatchlistPage />
        ) : activePage === 'market' ? (
          <MarketDashboard
            marketData={marketData}
            rules={rules}
            onGoToJournal={(prefill) => {
              setJournalPrefill(prefill);
              setActivePage('journal');
            }}
          />
        ) : activePage === 'coingecko' ? (
          <CoinGeckoPage />
        ) : activePage === 'rules' ? (
          <RuleSignalPage marketData={marketData} isAdmin={isAdmin} />
        ) : activePage === 'journal' ? (
          <TradeJournalPage
            marketData={marketData}
            isAdmin={isAdmin}
            prefill={journalPrefill}
            onClearPrefill={() => setJournalPrefill(null)}
          />
        ) : activePage === 'stats' ? (
          <EdgeStatsPage />
        ) : activePage === 'news' ? (
          <NewsPage />
        ) : (
          <DashboardModulesProvider
            tasks={tasks}
            isAdmin={isAdmin}
            onCreateTask={() => handleOpenModal()}
            onEditTask={handleOpenModal}
            onDeleteTask={createAdminGuardedAction(setTaskToDelete)}
            onToggleTaskComplete={handleToggleCompleteCallback}
          >
            <DashboardBoard isAdmin={isAdmin} />
          </DashboardModulesProvider>
        )}
      </main>

      {isAdmin && isModalOpen && <TaskModal task={taskToEdit} onClose={handleCloseModal} onSave={handleSaveTaskCallback} />}
      {isAdmin && <ConfirmationModal isOpen={!!taskToDelete} onClose={() => setTaskToDelete(null)} onConfirm={handleConfirmDeleteCallback} title="Delete Mission" message="Are you sure you want to delete this mission? This action cannot be undone." />}
      {isAdmin && <button onClick={() => handleOpenModal()} className="dashboard-fab-accent fixed bottom-8 right-8 rounded-full p-4 shadow-lg" aria-label="Add new mission"><PlusIcon className="w-8 h-8" /></button>}
    </div>
  );
};

export default App;
